// Source Reliability Engine — pure, client-safe.
// Scores every collector that contributed to a scan on a 1–5 star scale and
// explains the score across four axes: trustworthiness, recency,
// corroboration and contradictions.

export type SourceClass =
  | "government"
  | "regulated"
  | "authoritative"
  | "platform"
  | "aggregator"
  | "community"
  | "unvetted";

export type ReliabilityAxis = {
  label: string;
  value: string;
  detail: string;
  /** -2 … +2 contribution to the star rating. */
  delta: number;
};

export type SourceReliability = {
  source: string;
  sourceClass: SourceClass;
  classLabel: string;
  /** Final rating, 1–5 (integer stars) after adjustments. */
  stars: number;
  /** Base trust of the source class before evidence adjustments. */
  baseStars: number;
  status: "ok" | "miss" | "error";
  summary: string;
  latencyMs: number;
  /** Most recent dated evidence found inside the payload, if any. */
  evidenceDate: string | null;
  ageDays: number | null;
  corroboratedBy: string[];
  contradictions: Array<{ field: string; ours: string; theirs: string; source: string }>;
  axes: ReliabilityAxis[];
  verdict: string;
};

const CLASS_LABEL: Record<SourceClass, string> = {
  government: "Government / official registry",
  regulated: "Regulated filing or court record",
  authoritative: "Curated authoritative dataset",
  platform: "Platform-owned primary data",
  aggregator: "Third-party aggregator / index",
  community: "Community / user-generated",
  unvetted: "Unvetted heuristic sweep",
};

const CLASS_BASE: Record<SourceClass, number> = {
  government: 5,
  regulated: 5,
  authoritative: 4,
  platform: 3,
  aggregator: 3,
  community: 2,
  unvetted: 1,
};

const CLASS_BASIS: Record<SourceClass, string> = {
  government: "Maintained by a state body or official registry operator with a statutory duty of accuracy.",
  regulated: "Filed under legal obligation (courts, securities regulators); false statements carry legal consequence.",
  authoritative: "Curated dataset with documented methodology, editorial review or cryptographic provenance.",
  platform: "First-party data served by the platform that owns the account or artefact.",
  aggregator: "Re-publishes other people's data; accurate on existence, weaker on interpretation.",
  community: "Written by anonymous or pseudonymous users with no verification layer.",
  unvetted: "Heuristic/dork sweep output — treated as lead generation only, never as evidence.",
};

const RULES: Array<{ re: RegExp; cls: SourceClass }> = [
  { re: /OFAC|EU sanctions|UK HMT|FBI Wanted|GovInfo|SEC EDGAR|CourtListener|PACER|Companies House|registry|RDAP|WHOIS|IANA|Federal Register/i, cls: "government" },
  { re: /OpenCorporates|GLEIF|filing|docket|court|EDGAR/i, cls: "regulated" },
  { re: /OpenSanctions|HaveIBeenPwned|Certificate Transparency|crt\.sh|CertSpotter|OpenAlex|Crossref|arXiv|Wikidata|Wayback|URLhaus|PhishStats|AlienVault|Shodan|BGP|RIPE|Tor exit|Ethplorer|Blockchain|DNS /i, cls: "authoritative" },
  { re: /GitHub|GitLab|Bitbucket|Keybase|Reddit profile|Mastodon|DEV\.to|Hacker News profile|profile$|IP geolocation|HTTP headers|robots\.txt|security\.txt|sitemap\.xml|humans\.txt|ads\.txt/i, cls: "platform" },
  { re: /GDELT|Google News|news|Wikipedia|DuckDuckGo|Common Crawl|search$/i, cls: "aggregator" },
  { re: /Reddit search|StackExchange|Hacker News|forum|comment/i, cls: "community" },
  { re: /dork|paste|scrape|heuristic|Investigator/i, cls: "unvetted" },
];

export function classifySource(source: string): SourceClass {
  for (const r of RULES) if (r.re.test(source)) return r.cls;
  return "aggregator";
}

type Collectorish = {
  source: string;
  status?: string;
  summary?: string;
  latencyMs?: number;
  data?: unknown;
};

const STOP = new Set([
  "the", "and", "for", "with", "from", "this", "that", "returned", "public", "source",
  "data", "found", "results", "result", "record", "records", "none", "http", "https",
  "www", "com", "org", "net", "scan", "target", "latency", "match", "matches", "hits",
]);

function tokens(text: string): Set<string> {
  const out = new Set<string>();
  for (const raw of text.toLowerCase().split(/[^a-z0-9.-]+/)) {
    const t = raw.replace(/^[.-]+|[.-]+$/g, "");
    if (t.length < 4 || STOP.has(t) || /^\d+$/.test(t)) continue;
    out.add(t);
  }
  return out;
}

function walk(value: unknown, visit: (key: string, val: string | number) => void, depth = 0): void {
  if (depth > 6 || value == null) return;
  if (Array.isArray(value)) {
    for (const v of value.slice(0, 40)) walk(v, visit, depth + 1);
    return;
  }
  if (typeof value === "object") {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (typeof v === "string" || typeof v === "number") visit(k, v);
      else walk(v, visit, depth + 1);
    }
  }
}

const DATE_KEY = /date|_at$|time|created|updated|registered|expiry|expires|published|issued|last|first|seen/i;

function extractLatestDate(c: Collectorish): string | null {
  let best: number | null = null;
  const now = Date.now();
  const consider = (raw: string) => {
    const t = Date.parse(raw);
    if (!Number.isFinite(t)) return;
    // ignore absurd values and future timestamps beyond a day
    if (t < Date.parse("1970-01-02") || t > now + 86_400_000) return;
    if (best === null || t > best) best = t;
  };
  walk(c.data, (k, v) => {
    if (typeof v === "string" && (DATE_KEY.test(k) || /^\d{4}-\d{2}-\d{2}/.test(v))) consider(v);
  });
  const m = (c.summary ?? "").match(/\b(19|20)\d{2}-\d{2}-\d{2}\b/);
  if (m) consider(m[0]);
  return best === null ? null : new Date(best).toISOString();
}

const COMPARE_FIELDS: Array<{ key: RegExp; label: string }> = [
  { key: /^country(_code|_name)?$/i, label: "country" },
  { key: /^(jurisdiction|jurisdiction_code)$/i, label: "jurisdiction" },
  { key: /^(status|company_status|domain_status)$/i, label: "status" },
  { key: /^(registrar|registrarName)$/i, label: "registrar" },
  { key: /^(org|organization|organisation|company_name|legalName|name)$/i, label: "organisation name" },
  { key: /^(asn|as|autonomous_system)$/i, label: "network / ASN" },
];

function extractClaims(c: Collectorish): Map<string, string> {
  const out = new Map<string, string>();
  walk(c.data, (k, v) => {
    const s = String(v).trim();
    if (!s || s.length > 80) return;
    for (const f of COMPARE_FIELDS) {
      if (f.key.test(k) && !out.has(f.label)) out.set(f.label, s);
    }
  });
  return out;
}

function normalizeClaim(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function starsFromDelta(base: number, delta: number): number {
  return Math.max(1, Math.min(5, Math.round(base + delta)));
}

function fmtAge(days: number | null): string {
  if (days === null) return "undated";
  if (days < 1) return "today";
  if (days < 45) return `${Math.round(days)} days old`;
  if (days < 730) return `${Math.round(days / 30)} months old`;
  return `${(days / 365).toFixed(1)} years old`;
}

export function scoreSources(collectors: Collectorish[]): SourceReliability[] {
  const usable = (collectors ?? []).filter((c) => c && typeof c.source === "string");
  const enriched = usable.map((c) => ({
    c,
    tok: tokens(`${c.summary ?? ""} ${JSON.stringify(c.data ?? {}).slice(0, 4000)}`),
    claims: extractClaims(c),
  }));

  const out: SourceReliability[] = enriched.map(({ c, tok, claims }) => {
    const cls = classifySource(c.source);
    const base = CLASS_BASE[cls];
    const status = (c.status as SourceReliability["status"]) ?? "ok";

    // Corroboration: other sources sharing distinctive tokens.
    const corroboratedBy: string[] = [];
    for (const other of enriched) {
      if (other.c === c || other.c.source === c.source) continue;
      let shared = 0;
      for (const t of tok) if (other.tok.has(t)) shared++;
      if (shared >= 3) corroboratedBy.push(other.c.source);
    }

    // Contradictions: same field, different value, from a peer source.
    const contradictions: SourceReliability["contradictions"] = [];
    for (const other of enriched) {
      if (other.c === c || other.c.source === c.source) continue;
      for (const [field, ours] of claims) {
        const theirs = other.claims.get(field);
        if (!theirs) continue;
        if (normalizeClaim(theirs) !== normalizeClaim(ours)) {
          contradictions.push({ field, ours, theirs, source: other.c.source });
        }
      }
    }
    const dedupedContradictions = contradictions
      .filter((x, i, arr) => arr.findIndex((y) => y.field === x.field && y.source === x.source) === i)
      .slice(0, 6);

    const evidenceDate = extractLatestDate(c);
    const ageDays = evidenceDate ? (Date.now() - Date.parse(evidenceDate)) / 86_400_000 : null;

    // ---- axis deltas ----
    let delta = 0;

    const trustAxis: ReliabilityAxis = {
      label: "Trustworthiness",
      value: `${base}/5 baseline · ${CLASS_LABEL[cls]}`,
      detail: CLASS_BASIS[cls],
      delta: 0,
    };

    let recencyDelta = 0;
    let recencyDetail: string;
    if (ageDays === null) {
      recencyDelta = -0.4;
      recencyDetail = "No dated evidence in the payload, so the claim cannot be time-anchored.";
    } else if (ageDays <= 90) {
      recencyDelta = 0.5;
      recencyDetail = "Freshly dated evidence — reflects the current state of the record.";
    } else if (ageDays <= 730) {
      recencyDelta = 0;
      recencyDetail = "Recent enough to be treated as current, but re-verify before relying on it.";
    } else {
      recencyDelta = -0.6;
      recencyDetail = "Stale record — the underlying facts may have changed since publication.";
    }
    const recencyAxis: ReliabilityAxis = {
      label: "Recency",
      value: evidenceDate ? `${fmtAge(ageDays)} · ${evidenceDate.slice(0, 10)}` : "Undated",
      detail: recencyDetail,
      delta: recencyDelta,
    };

    const corrDelta = corroboratedBy.length >= 3 ? 0.8 : corroboratedBy.length >= 1 ? 0.4 : -0.3;
    const corrAxis: ReliabilityAxis = {
      label: "Corroboration",
      value: corroboratedBy.length
        ? `${corroboratedBy.length} independent source${corroboratedBy.length === 1 ? "" : "s"}`
        : "Uncorroborated",
      detail: corroboratedBy.length
        ? `Overlapping facts also appear in: ${corroboratedBy.slice(0, 5).join(", ")}${corroboratedBy.length > 5 ? "…" : ""}.`
        : "No other collector reproduced these facts — treat as a single-source claim.",
      delta: corrDelta,
    };

    const contraDelta = dedupedContradictions.length ? -Math.min(1.5, 0.6 * dedupedContradictions.length) : 0.2;
    const contraAxis: ReliabilityAxis = {
      label: "Contradictions",
      value: dedupedContradictions.length ? `${dedupedContradictions.length} conflict${dedupedContradictions.length === 1 ? "" : "s"}` : "None detected",
      detail: dedupedContradictions.length
        ? dedupedContradictions
            .map((x) => `${x.field}: "${x.ours}" here vs "${x.theirs}" in ${x.source}`)
            .join(" · ")
        : "No peer source disagrees with the values returned here.",
      delta: contraDelta,
    };

    delta = recencyDelta + corrDelta + contraDelta;
    if (status === "miss") delta -= 0.5;
    if (status === "error") delta -= 1;

    const stars = starsFromDelta(base, delta);

    const verdict = buildVerdict(cls, stars, corroboratedBy.length, dedupedContradictions.length, ageDays);

    return {
      source: c.source,
      sourceClass: cls,
      classLabel: CLASS_LABEL[cls],
      stars,
      baseStars: base,
      status,
      summary: c.summary ?? "",
      latencyMs: c.latencyMs ?? 0,
      evidenceDate,
      ageDays: ageDays === null ? null : Math.round(ageDays),
      corroboratedBy: corroboratedBy.slice(0, 8),
      contradictions: dedupedContradictions,
      axes: [trustAxis, recencyAxis, corrAxis, contraAxis],
      verdict,
    };
  });

  return out.sort((a, b) => b.stars - a.stars || a.source.localeCompare(b.source));
}

function buildVerdict(cls: SourceClass, stars: number, corr: number, contra: number, ageDays: number | null): string {
  const head =
    stars >= 5 ? "Rely on this directly."
      : stars === 4 ? "Strong evidence; cite with a second source."
      : stars === 3 ? "Usable as a lead, verify before publishing."
      : stars === 2 ? "Weak signal — corroborate before any conclusion."
      : "Lead generation only; not evidence.";
  const bits: string[] = [CLASS_LABEL[cls].toLowerCase()];
  bits.push(corr ? `corroborated by ${corr}` : "single-source");
  if (contra) bits.push(`${contra} contradiction${contra === 1 ? "" : "s"}`);
  if (ageDays !== null && ageDays > 730) bits.push("stale");
  return `${head} (${bits.join(", ")})`;
}

export function reliabilityOverview(rows: SourceReliability[]): {
  avgStars: number;
  highTrust: number;
  contradicted: number;
  uncorroborated: number;
} {
  if (!rows.length) return { avgStars: 0, highTrust: 0, contradicted: 0, uncorroborated: 0 };
  const avg = rows.reduce((s, r) => s + r.stars, 0) / rows.length;
  return {
    avgStars: Math.round(avg * 10) / 10,
    highTrust: rows.filter((r) => r.stars >= 4).length,
    contradicted: rows.filter((r) => r.contradictions.length > 0).length,
    uncorroborated: rows.filter((r) => r.corroboratedBy.length === 0).length,
  };
}
